import React, { useState, useEffect, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Calendar as CalendarIcon,
  Send,
  Users,
  MapPin,
  Clock,
  Trash2,
  Edit,
  X,
  Check,
  CheckSquare,
  ClipboardList,
  Copy,
  CopyPlus,
  ClipboardPaste,
  Maximize2,
  Minimize2
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigate } from "react-router-dom";
import { CalendarCommandBar } from "@/components/calendar/CalendarCommandBar";

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(BigCalendar);

export const EVENT_TYPES = [
  { value: "site_measure", label: "Site Measure", color: "#0ea5e9" },
  { value: "design_consult", label: "Design Consultation", color: "#8b5cf6" },
  { value: "shop_fabrication", label: "Shop Fabrication", color: "#f59e0b" },
  { value: "crew_installation", label: "Crew Installation", color: "#10b981" },
  { value: "delivery", label: "Delivery", color: "#6366f1" },
  { value: "service_call", label: "Service Call", color: "#ef4444" },
  { value: "other", label: "Other", color: "#64748b" },
];
const eventTypeMeta = (v) => EVENT_TYPES.find(t => t.value === v) || EVENT_TYPES[EVENT_TYPES.length - 1];

export default function CalendarPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Modals
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null); // {date, events}
  const [pasteMode, setPasteMode] = useState(null); // {source, dates: []}
  const [contextMenu, setContextMenu] = useState(null); // {x, y, schedule}
  const ctrlHeld = React.useRef(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [fullScreen, setFullScreen] = useState(false);
  const [dayMenu, setDayMenu] = useState(null); // {x, y, date}
  const [pasting, setPasting] = useState(false);
  const pasteModeRef = React.useRef(null);
  const pasteFnRef = React.useRef(() => {});
  const [deletingDrafts, setDeletingDrafts] = useState(false);
  useEffect(() => { pasteModeRef.current = pasteMode; }, [pasteMode]);
  
  // Form states
  const [newSchedule, setNewSchedule] = useState({
    project_id: "",
    title: "",
    description: "",
    date: "",
    end_date: "",
    event_type: "other",
    start_time: "08:00",
    end_time: "17:00",
    assigned_users: [],
    notes: ""
  });
  
  // Task assignment for schedule
  const [projectTasks, setProjectTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [showTaskPickerModal, setShowTaskPickerModal] = useState(false);
  
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    address: "",
    color: "#6366f1",
    budget: 0
  });
  
  const [selectedPublishIds, setSelectedPublishIds] = useState([]);

  const isOwner = user?.role === 'owner';
  const isManager = isOwner || user?.role === 'project_manager' || user?.role === 'office_admin';
  const canCreate = isManager; // PM + Office Admin + Owner can create

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/schedules`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] }))
      ];
      
      if (isManager) {
        requests.push(axios.get(`${API}/team`).catch(() => ({ data: [] })));
      }
      
      const responses = await Promise.all(requests);
      setSchedules(responses[0].data || []);
      setProjects(responses[1].data || []);
      if (isManager && responses[2]) {
        setTeamMembers(responses[2].data || []);
      }
    } catch (error) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  // Convert schedules to calendar events
  const calendarEvents = schedules
    .filter(s => /^\d{4}-\d{2}-\d{2}$/.test(s.date || "") && (typeFilter === "all" || (s.event_type || "other") === typeFilter))
    .map(schedule => {
      const project = schedule.project || projects.find(p => p.project_id === schedule.project_id);
      const startDateTime = moment(`${schedule.date} ${schedule.start_time}`, 'YYYY-MM-DD HH:mm').toDate();
      const lastDay = schedule.end_date && schedule.end_date > schedule.date ? schedule.end_date : schedule.date;
      const endDateTime = moment(`${lastDay} ${schedule.end_time}`, 'YYYY-MM-DD HH:mm').toDate();
      const meta = eventTypeMeta(schedule.event_type);
      return {
        id: schedule.schedule_id,
        title: schedule.event_type && schedule.event_type !== "other" ? `${meta.label.split(" ")[0]}: ${schedule.title}` : schedule.title,
        start: startDateTime,
        end: endDateTime,
        allDay: !!schedule.all_day || lastDay !== schedule.date,
        resource: schedule,
        color: schedule.color || (schedule.event_type && schedule.event_type !== "other" ? meta.color : project?.color) || '#6366f1'
      };
    });

  const handleSelectSlot = useCallback(({ start, slots }) => {
    if (pasteMode) {
      const days = (slots && slots.length ? slots : [start]).map(d => moment(d).format("YYYY-MM-DD"));
      setPasteMode(pm => {
        const set = new Set(pm.dates);
        const allIn = days.every(d => set.has(d));
        days.forEach(d => allIn ? set.delete(d) : set.add(d));
        return { ...pm, dates: [...set].sort() };
      });
      return;
    }
    const dayStr = moment(start).format("YYYY-MM-DD");
    const dayEvents = schedules.filter(s => (s.date || "").startsWith(dayStr));
    setSelectedDay({ date: dayStr, events: dayEvents, dateObj: start });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules, pasteMode]);

  const handleSelectEvent = useCallback((event) => {
    if (pasteMode) return;
    setShowEventDetail(event.resource);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pasteMode]);

  // ---- Drag & drop / copy-paste ----
  useEffect(() => {
    const down = (e) => {
      if (e.key === "Control" || e.key === "Meta") ctrlHeld.current = true;
      if (e.key === "Escape") { setPasteMode(null); setContextMenu(null); setDayMenu(null); setFullScreen(false); }
      if (e.key === "Enter" && pasteModeRef.current?.dates?.length && !e.target.closest("input,textarea,select,[role=dialog]")) { e.preventDefault(); pasteFnRef.current(); }
    };
    const up = (e) => { if (e.key === "Control" || e.key === "Meta") ctrlHeld.current = false; };
    const click = () => { setContextMenu(null); setDayMenu(null); };
    window.addEventListener("keydown", down); window.addEventListener("keyup", up); window.addEventListener("click", click);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); window.removeEventListener("click", click); };
  }, []);

  const undoCopy = async (created, toastId) => {
    toast.dismiss(toastId);
    try {
      const r = await axios.post(`${API}/schedules/undo-copy`, { schedule_ids: created.map(c => c.schedule_id) });
      toast.success(`Undo complete — removed ${r.data.removed} pasted entr${r.data.removed === 1 ? "y" : "ies"}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Undo failed"); }
  };

  const showPasteToast = (title, created, tasksCopied) => {
    const n = created.length;
    const id = toast.success(`Pasted "${title}" to ${n} day${n === 1 ? "" : "s"}`, {
      description: `${tasksCopied ? `${tasksCopied} task(s) copied · ` : ""}Copies are unpublished — publish when ready`,
      duration: 10000,
      action: { label: "Undo", onClick: () => undoCopy(created, id) },
    });
  };

  const copyScheduleTo = async (schedule, dates) => {
    try {
      const res = await axios.post(`${API}/schedules/${schedule.schedule_id}/copy`, { dates });
      showPasteToast(schedule.title, res.data.created, res.data.tasks_copied);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to copy"); }
  };

  const handleEventDrop = async ({ event, start, end, isAllDay }) => {
    if (!canCreate) return;
    const s = event.resource;
    const newDate = moment(start).format("YYYY-MM-DD");
    if (ctrlHeld.current) return copyScheduleTo(s, [newDate]);
    const sameDayOnly = view === "month" || isAllDay;
    const patch = { date: newDate };
    if (s.end_date && s.end_date > s.date) {
      const span = moment(s.end_date).diff(moment(s.date), "days");
      patch.end_date = moment(newDate).add(span, "days").format("YYYY-MM-DD");
    }
    if (!sameDayOnly) { patch.start_time = moment(start).format("HH:mm"); patch.end_time = moment(end).format("HH:mm"); }
    if (patch.date === s.date && (sameDayOnly || (patch.start_time === s.start_time && patch.end_time === s.end_time))) return;
    setSchedules(prev => prev.map(x => x.schedule_id === s.schedule_id ? { ...x, ...patch, is_published: false } : x));
    try {
      await axios.put(`${API}/schedules/${s.schedule_id}`, patch);
      toast.success(`Moved "${s.title}" to ${moment(newDate).format("ddd, MMM D")}`, s.is_published ? { description: "It was published — re-publish so the crew sees the change" } : undefined);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to move"); fetchData(); }
  };

  const handleEventResize = async ({ event, start, end }) => {
    if (!canCreate) return;
    const s = event.resource;
    const patch = { start_time: moment(start).format("HH:mm"), end_time: moment(end).format("HH:mm") };
    setSchedules(prev => prev.map(x => x.schedule_id === s.schedule_id ? { ...x, ...patch, is_published: false } : x));
    try { await axios.put(`${API}/schedules/${s.schedule_id}`, patch); toast.success(`"${s.title}" now ${patch.start_time} – ${patch.end_time}`); }
    catch (err) { toast.error("Failed to resize"); fetchData(); }
  };

  const startPasteMode = (schedule) => {
    setContextMenu(null); setShowEventDetail(null); setSelectedDay(null);
    setPasteMode({ source: schedule, dates: [] });
    toast.info(`Copying "${schedule.title}" — click the days to paste to, then hit Paste`);
  };

  const handlePaste = async () => {
    const pm = pasteModeRef.current;
    if (!pm?.dates.length) return toast.error("Pick at least one day on the calendar");
    if (pasting) return;
    setPasting(true);
    try {
      const res = await axios.post(`${API}/schedules/${pm.source.schedule_id}/copy`, { dates: pm.dates });
      showPasteToast(pm.source.title, res.data.created, res.data.tasks_copied);
      setPasteMode(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Paste failed — your selection is kept, try again");
    } finally { setPasting(false); }
  };
  pasteFnRef.current = handlePaste;

  const DayCellWrapper = React.useCallback(({ children, value }) => (
    <div className="contents" onContextMenu={(e) => {
      if (!pasteModeRef.current) return;
      e.preventDefault(); e.stopPropagation();
      setDayMenu({ x: e.clientX, y: e.clientY, date: moment(value).format("YYYY-MM-DD") });
    }}>{children}</div>
  ), []);

  const pasteDayPropGetter = (date) => {
    const d = moment(date).format("YYYY-MM-DD");
    if (pasteMode?.dates.includes(d)) return { style: { backgroundColor: "#c7d2fe", boxShadow: "inset 0 0 0 2px #6366f1" }, className: "paste-target" };
    if (pasteMode) return { style: { cursor: "copy" } };
    return {};
  };

  const calendarComponents = React.useMemo(() => ({
    dateCellWrapper: DayCellWrapper,
    event: ({ event, title }) => (
      <div className="h-full w-full" onContextMenu={(e) => { if (!canCreate) return; e.preventDefault(); e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY, schedule: event.resource }); }} data-testid={`cal-event-${event.id}`}>
        {title}
      </div>
    ),
  }), [canCreate, DayCellWrapper]);

  const fetchProjectTasks = async (projectId) => {
    if (!projectId) { setProjectTasks([]); return; }
    try {
      const res = await axios.get(`${API}/tasks/project-all/${projectId}`);
      setProjectTasks(res.data || []);
    } catch (err) {
      setProjectTasks([]);
    }
  };

  const handleAddQuickTask = () => {
    if (!newTaskTitle.trim()) return;
    setSelectedTasks([...selectedTasks, { title: newTaskTitle.trim(), isNew: true, assigned_to: [...newSchedule.assigned_users] }]);
    setNewTaskTitle("");
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      const exists = prev.find(t => t.task_id === taskId);
      if (exists) return prev.filter(t => t.task_id !== taskId);
      const task = projectTasks.find(t => t.task_id === taskId);
      if (task) return [...prev, { ...task, assigned_to: [...newSchedule.assigned_users] }];
      return prev;
    });
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    try {
      let scheduleId;
      if (editingSchedule) {
        await axios.put(`${API}/schedules/${editingSchedule.schedule_id}`, { ...newSchedule, end_date: newSchedule.end_date || null });
        scheduleId = editingSchedule.schedule_id;
        toast.success("Schedule updated");
      } else {
        const res = await axios.post(`${API}/schedules`, { ...newSchedule, end_date: newSchedule.end_date || null });
        scheduleId = res.data?.schedule_id;
        toast.success("Schedule created");
      }

      // Create new tasks and assign existing ones to this schedule
      for (const task of selectedTasks) {
        if (task.isNew) {
          await axios.post(`${API}/tasks`, {
            project_id: newSchedule.project_id,
            schedule_id: scheduleId,
            title: task.title,
            assigned_to: task.assigned_to || newSchedule.assigned_users,
          });
        } else if (task.task_id) {
          if (task.source === "checklist") {
            // Checklist items don't exist in tasks collection — create a real task
            await axios.post(`${API}/tasks`, {
              project_id: newSchedule.project_id,
              schedule_id: scheduleId,
              title: task.title,
              assigned_to: task.assigned_to || newSchedule.assigned_users,
            }).catch(() => {});
          } else {
            await axios.put(`${API}/tasks/${task.task_id}`, {
              schedule_id: scheduleId,
              assigned_to: task.assigned_to || newSchedule.assigned_users,
            }).catch(() => {});
          }
        }
      }
      fetchData();
      setShowScheduleModal(false);
      resetScheduleForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save schedule");
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm("Are you sure you want to delete this schedule?")) return;
    try {
      const res = await axios.delete(`${API}/schedules/${scheduleId}`);
      if (res.data?.detail?.includes("approval")) {
        toast.info("Delete request sent to owner for approval");
      } else {
        toast.success("Schedule moved to trash");
      }
      setShowEventDetail(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to delete schedule";
      if (msg.includes("approval")) {
        toast.info(msg);
        setShowEventDetail(null);
      } else {
        toast.error(msg);
      }
    }
  };

  const handlePublishSchedules = async () => {
    if (selectedPublishIds.length === 0) {
      toast.error("Select schedules to publish");
      return;
    }
    try {
      const response = await axios.post(`${API}/schedules/publish`, {
        schedule_ids: selectedPublishIds,
        notify_sms: true,
        notify_email: true
      });
      toast.success(response.data.message);
      setSelectedPublishIds([]);
      fetchData();
    } catch (error) {
      toast.error("Failed to publish schedules");
    }
  };

  const handleDeleteSelectedDrafts = async () => {
    if (selectedPublishIds.length === 0 || deletingDrafts) return;
    setDeletingDrafts(true);
    try {
      const response = await axios.post(`${API}/schedules/bulk-delete-drafts`, {
        schedule_ids: selectedPublishIds,
      });
      toast.success(`Deleted ${response.data.removed} draft schedule${response.data.removed === 1 ? "" : "s"}`);
      if (response.data.skipped) {
        toast.info(`${response.data.skipped} item${response.data.skipped === 1 ? " was" : "s were"} skipped because they are no longer drafts`);
      }
      setSelectedPublishIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete selected drafts");
    } finally {
      setDeletingDrafts(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, newProject);
      toast.success("Project created");
      fetchData();
      setShowProjectModal(false);
      setNewProject({
        name: "",
        description: "",
        client_name: "",
        client_email: "",
        client_phone: "",
        address: "",
        color: "#6366f1",
        budget: 0
      });
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const resetScheduleForm = () => {
    setNewSchedule({
      project_id: "",
      title: "",
      description: "",
      date: "",
      end_date: "",
      event_type: "other",
      start_time: "08:00",
      end_time: "17:00",
      assigned_users: [],
      notes: ""
    });
    setEditingSchedule(null);
    setSelectedTasks([]);
    setProjectTasks([]);
    setNewTaskTitle("");
  };

  const handleEditSchedule = async (schedule) => {
    setNewSchedule({
      project_id: schedule.project_id,
      title: schedule.title,
      description: schedule.description || "",
      date: schedule.date,
      end_date: schedule.end_date || "",
      event_type: schedule.event_type || "other",
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      assigned_users: schedule.assigned_users || [],
      notes: schedule.notes || ""
    });
    setEditingSchedule(schedule);
    setShowEventDetail(null);
    setSelectedDay(null);
    setShowScheduleModal(true);

    // Load project tasks and schedule's assigned tasks
    if (schedule.project_id) {
      fetchProjectTasks(schedule.project_id);
    }
    // Load tasks already assigned to this schedule
    try {
      const res = await axios.get(`${API}/tasks?schedule_id=${schedule.schedule_id}`).catch(() => ({ data: [] }));
      const scheduleTasks = res.data || [];
      setSelectedTasks(scheduleTasks.map(t => ({ ...t, task_id: t.task_id })));
    } catch (err) {
      setSelectedTasks([]);
    }
  };

  const toggleUserAssignment = (userId) => {
    setNewSchedule(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      borderRadius: '4px',
      opacity: event.resource?.is_published ? 1 : 0.7,
      border: event.resource?.is_published ? '2px solid #22c55e' : '2px dashed #94a3b8',
      color: 'white',
      fontSize: '12px'
    }
  });

  const unpublishedSchedules = schedules.filter(s => !s.is_published);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="calendar-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="calendar-page">
      <section className="flex h-[calc(100dvh-6rem)] min-h-[34rem] flex-col gap-2 md:h-[calc(100dvh-7rem)]" data-testid="calendar-viewport">
        <CalendarCommandBar
          eventCount={schedules.length}
          projectCount={projects.length}
          isManager={isManager}
          unpublishedSchedules={unpublishedSchedules}
          selectedIds={selectedPublishIds}
          deleting={deletingDrafts}
          onNewProject={() => setShowProjectModal(true)}
          onNewSchedule={() => { resetScheduleForm(); setShowScheduleModal(true); }}
          onPublish={handlePublishSchedules}
          onDeleteSelected={handleDeleteSelectedDrafts}
          onSelectionChange={setSelectedPublishIds}
        />

      {/* Paste-mode banner */}
      {pasteMode && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2" data-testid="paste-banner">
          <div className="flex items-center gap-3 min-w-0">
            <ClipboardPaste className="w-5 h-5 text-indigo-600 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-indigo-900 truncate">Copying "{pasteMode.source.title}"</p>
              <p className="text-xs text-indigo-700">Click days to select · right-click a day to paste there · Enter to paste{pasteMode.dates.length ? ` · ${pasteMode.dates.length} selected: ${pasteMode.dates.map(d => moment(d).format("MMM D")).join(", ")}` : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPasteMode(null)} data-testid="paste-cancel-btn"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button size="sm" onClick={handlePaste} disabled={!pasteMode.dates.length || pasting} data-testid="paste-confirm-btn"><ClipboardPaste className="w-4 h-4 mr-1" /> Paste to {pasteMode.dates.length || ""} day{pasteMode.dates.length === 1 ? "" : "s"} <kbd className="ml-2 text-[10px] bg-white/70 text-indigo-800 border rounded px-1">Enter</kbd></Button>
          </div>
        </div>
      )}

      {/* Calendar */}
      {(() => { const calendarCard = (
      <Card data-testid="schedule-calendar" className={`schedule-calendar-frame min-h-0 flex-1 overflow-hidden ${pasteMode ? "ring-2 ring-indigo-300" : ""} ${fullScreen ? "h-screen rounded-none border-0 shadow-none" : ""}`}>
        <CardContent className="flex h-full min-h-0 flex-col p-2">
          <div className="mb-1 flex min-h-7 items-center gap-2">
            <div className="min-w-0 flex-1" data-testid="event-type-filter">
              <select
                className="h-7 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 md:hidden"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                data-testid="mobile-event-type-filter"
              >
                <option value="all">All event types</option>
                {EVENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
              <div className="hidden min-w-0 flex-nowrap items-center gap-1.5 md:flex">
              <button onClick={() => setTypeFilter("all")} data-testid="type-filter-all" className={`h-6 shrink-0 rounded-full border px-2.5 text-xs ${typeFilter === "all" ? "border-slate-800 bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>All types</button>
              {EVENT_TYPES.map(t => (
                <button key={t.value} onClick={() => setTypeFilter(typeFilter === t.value ? "all" : t.value)} data-testid={`type-filter-${t.value}`}
                  className={`flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-xs ${typeFilter === t.value ? "text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  style={typeFilter === t.value ? { backgroundColor: t.color, borderColor: t.color } : {}}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} /> {t.label}
                </button>
              ))}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setFullScreen(f => !f)} data-testid="calendar-fullscreen-btn" title={fullScreen ? "Exit full screen (Esc)" : "Full screen"}>
              {fullScreen ? <><Minimize2 className="w-3.5 h-3.5 mr-1" /> Exit full screen</> : <><Maximize2 className="w-3.5 h-3.5 mr-1" /> Full screen</>}
            </Button>
          </div>
          <div className="min-h-0 flex-1" data-testid="calendar-container">
            <DnDCalendar
              popup={true}
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={setView}
              date={currentDate}
              onNavigate={setCurrentDate}
              selectable={true}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onEventDrop={handleEventDrop}
              onEventResize={handleEventResize}
              draggableAccessor={() => canCreate && !pasteMode}
              resizable={canCreate && !pasteMode}
              dayPropGetter={pasteDayPropGetter}
              components={calendarComponents}
              onDrillDown={(date) => {
                if (pasteMode) return handleSelectSlot({ start: date, slots: [date] });
                const dayStr = moment(date).format("YYYY-MM-DD");
                const dayEvents = schedules.filter(s => (s.date || "").startsWith(dayStr));
                setSelectedDay({ date: dayStr, events: dayEvents, dateObj: date });
              }}
              eventPropGetter={eventStyleGetter}
              views={['month', 'week', 'day', 'agenda']}
              step={30}
              timeslots={2}
              min={new Date(2024, 0, 1, 6, 0)}
              max={new Date(2024, 0, 1, 20, 0)}
              formats={{
                eventTimeRangeFormat: () => null
              }}
            />
          </div>
        </CardContent>
      </Card>
      ); return fullScreen ? createPortal(<div className="fixed inset-0 z-[1000] overflow-hidden bg-white" data-testid="calendar-fullscreen">{calendarCard}</div>, document.body) : calendarCard; })()}
      </section>

      {/* Right-click on a day while copying */}
      {dayMenu && pasteMode && (
        <div className="fixed z-[100] w-64 rounded-lg border bg-white shadow-xl py-1 text-sm animate-fade-in" style={{ top: Math.min(dayMenu.y, window.innerHeight - 170), left: Math.min(dayMenu.x, window.innerWidth - 270) }}
          onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()} data-testid="day-context-menu">
          <p className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b">{moment(dayMenu.date).format("ddd, MMM D")} · copying "{pasteMode.source.title}"</p>
          <button className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2" onClick={() => { const d = dayMenu.date; setDayMenu(null); copyScheduleTo(pasteMode.source, [d]); }} data-testid="ctx-paste-here"><ClipboardPaste className="w-4 h-4 text-indigo-600" /> Paste copied event here</button>
          {pasteMode.dates.length > 0 && <button className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2" onClick={() => { setDayMenu(null); handlePaste(); }} data-testid="ctx-paste-selected"><ClipboardPaste className="w-4 h-4 text-indigo-600" /> Paste to {pasteMode.dates.length} selected day{pasteMode.dates.length === 1 ? "" : "s"}</button>}
          <button className="w-full text-left px-3 py-2 hover:bg-slate-50 text-slate-500 flex items-center gap-2" onClick={() => { setPasteMode(null); setDayMenu(null); }}><X className="w-4 h-4" /> Cancel copying</button>
        </div>
      )}

      {/* Right-click context menu */}
      {contextMenu && (
        <div className="fixed z-[100] w-52 rounded-lg border bg-white shadow-xl py-1 text-sm animate-fade-in" style={{ top: Math.min(contextMenu.y, window.innerHeight - 200), left: Math.min(contextMenu.x, window.innerWidth - 220) }}
          onClick={(e) => e.stopPropagation()} onContextMenu={(e) => e.preventDefault()} data-testid="event-context-menu">
          <p className="px-3 py-1.5 text-xs font-semibold text-slate-500 truncate border-b">{contextMenu.schedule.title}</p>
          <button className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2" onClick={() => startPasteMode(contextMenu.schedule)} data-testid="ctx-copy"><Copy className="w-4 h-4 text-indigo-600" /> Copy to other days…</button>
          <button className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2" onClick={() => { const s = contextMenu.schedule; setContextMenu(null); copyScheduleTo(s, [moment(s.date).add(1, "day").format("YYYY-MM-DD")]); }} data-testid="ctx-duplicate-tomorrow"><CopyPlus className="w-4 h-4 text-indigo-600" /> Duplicate to next day</button>
          <button className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center gap-2" onClick={() => { const s = contextMenu.schedule; setContextMenu(null); handleEditSchedule(s); }} data-testid="ctx-edit"><Edit className="w-4 h-4 text-slate-600" /> Edit</button>
          {isManager && <button className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2" onClick={() => { const s = contextMenu.schedule; setContextMenu(null); handleDeleteSchedule(s.schedule_id); }} data-testid="ctx-delete"><Trash2 className="w-4 h-4" /> {isOwner ? "Delete" : "Request delete"}</button>}
        </div>
      )}

      {/* Projects Overview */}
      <Card className="mt-6" data-testid="projects-overview">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Projects</span>
            <Badge variant="secondary">{projects.length} active</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map(project => (
                <div 
                  key={project.project_id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  style={{ borderLeftColor: project.color, borderLeftWidth: 4 }}
                  data-testid={`project-card-${project.project_id}`}
                >
                  <h3 className="font-medium text-slate-900">{project.name}</h3>
                  {project.client_name && (
                    <p className="text-sm text-slate-600 mt-1">Client: {project.client_name}</p>
                  )}
                  {project.address && (
                    <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {project.address}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <Badge className="capitalize">{project.status}</Badge>
                    <span className="text-xs text-slate-400">
                      {schedules.filter(s => s.project_id === project.project_id).length} scheduled
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No projects yet</p>
              {isManager && (
                <Button variant="outline" className="mt-4" onClick={() => setShowProjectModal(true)}>
                  Create First Project
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Detail Modal */}
      <Dialog open={!!showEventDetail} onOpenChange={() => setShowEventDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{showEventDetail?.title}</span>
              {showEventDetail?.is_published && (
                <Badge className="bg-green-100 text-green-700">Published</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {showEventDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Date</Label>
                  <p className="font-medium">{moment(showEventDetail.date).format('ddd, MMM D, YYYY')}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Time</Label>
                  <p className="font-medium">{showEventDetail.start_time} - {showEventDetail.end_time}</p>
                </div>
              </div>
              
              {showEventDetail.project && (
                <div>
                  <Label className="text-slate-500">Project</Label>
                  <p className="font-medium">{showEventDetail.project.name}</p>
                  {showEventDetail.project.address && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {showEventDetail.project.address}
                    </p>
                  )}
                </div>
              )}

              {showEventDetail.description && (
                <div>
                  <Label className="text-slate-500">Description</Label>
                  <p>{showEventDetail.description}</p>
                </div>
              )}

              {showEventDetail.assigned_users_info?.length > 0 && (
                <div>
                  <Label className="text-slate-500">Assigned Team</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {showEventDetail.assigned_users_info.map(member => (
                      <div 
                        key={member.user_id}
                        className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full"
                      >
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={member.picture} />
                          <AvatarFallback className="text-xs">
                            {member.name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{member.name}</span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {member.role?.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showEventDetail.notes && (
                <div>
                  <Label className="text-slate-500">Notes</Label>
                  <p className="text-sm">{showEventDetail.notes}</p>
                </div>
              )}

              {/* Assigned Tasks */}
              <EventTasks scheduleId={showEventDetail.schedule_id} projectId={showEventDetail.project_id} />

              <DialogFooter className="flex gap-2">
                {canCreate && (
                  <Button variant="outline" onClick={() => startPasteMode(showEventDetail)} data-testid="event-copy-btn">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy to dates…
                  </Button>
                )}
                {canCreate && (
                  <Button variant="outline" onClick={() => handleEditSchedule(showEventDetail)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
                {isManager && (
                  <Button 
                    variant="destructive" 
                    onClick={() => handleDeleteSchedule(showEventDetail.schedule_id)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {isOwner ? "Delete" : "Request Delete"}
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New/Edit Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSchedule} className="space-y-4">
            <div>
              <Label>Project *</Label>
              <Select
                value={newSchedule.project_id}
                onValueChange={(v) => { setNewSchedule({ ...newSchedule, project_id: v }); fetchProjectTasks(v); }}
              >
                <SelectTrigger data-testid="schedule-project">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: project.color }}
                        />
                        {project.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={newSchedule.title}
                onChange={(e) => setNewSchedule({ ...newSchedule, title: e.target.value })}
                placeholder="e.g., Kitchen Installation"
                required
                data-testid="schedule-title"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newSchedule.description}
                onChange={(e) => setNewSchedule({ ...newSchedule, description: e.target.value })}
                placeholder="What work will be done?"
                rows={2}
                data-testid="schedule-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Event Type *</Label>
                <select className="w-full border rounded-md px-3 py-2 text-sm bg-white h-10" value={newSchedule.event_type}
                  onChange={(e) => setNewSchedule({ ...newSchedule, event_type: e.target.value })} data-testid="schedule-event-type">
                  {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label>End Date <span className="text-slate-400 text-xs">(multi-day, optional)</span></Label>
                <Input type="date" value={newSchedule.end_date} min={newSchedule.date || undefined}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_date: e.target.value })} data-testid="schedule-end-date" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={newSchedule.date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, date: e.target.value })}
                  required
                  data-testid="schedule-date"
                />
              </div>
              <div>
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={newSchedule.start_time}
                  onChange={(e) => setNewSchedule({ ...newSchedule, start_time: e.target.value })}
                  required
                  data-testid="schedule-start-time"
                />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={newSchedule.end_time}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_time: e.target.value })}
                  required
                  data-testid="schedule-end-time"
                />
              </div>
            </div>

            {teamMembers.length > 0 && (
              <div>
                <Label>Assign Team Members</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {teamMembers.filter(m => m.role !== 'owner').map(member => (
                    <label
                      key={member.user_id}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        newSchedule.assigned_users.includes(member.user_id)
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={newSchedule.assigned_users.includes(member.user_id)}
                        onCheckedChange={() => toggleUserAssignment(member.user_id)}
                      />
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={member.picture} />
                        <AvatarFallback className="text-xs">
                          {member.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{member.name}</p>
                        <p className="text-xs text-slate-500 capitalize">{member.role?.replace('_', ' ')}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Task Assignment */}
            {newSchedule.project_id && (
              <div>
                <Label>Tasks</Label>
                <div className="mt-2 space-y-2">
                  {/* Selected tasks (from existing + new) */}
                  {selectedTasks.length > 0 && (
                    <div className="space-y-1">
                      {selectedTasks.map((task, idx) => (
                        <div key={task.task_id || `new-${idx}`} className={`flex items-center gap-2 text-sm rounded p-2 ${task.isNew ? "bg-green-50 border border-green-200" : "bg-indigo-50 border border-indigo-200"}`}>
                          <CheckSquare className={`w-4 h-4 shrink-0 ${task.isNew ? "text-green-600" : "text-indigo-600"}`} />
                          <span className="truncate flex-1">{task.title}</span>
                          {task.isNew && <Badge className="text-[10px] bg-green-100 text-green-700">New</Badge>}
                          <button type="button" onClick={() => setSelectedTasks(selectedTasks.filter(t => t !== task))} className="text-red-400 hover:text-red-600 shrink-0">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {/* Tasks Available button - only shows if project has tasks */}
                    {projectTasks.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        onClick={() => setShowTaskPickerModal(true)}
                        data-testid="tasks-available-btn"
                      >
                        <ClipboardList className="w-3 h-3 mr-1" />
                        Tasks Available ({projectTasks.filter(t => !selectedTasks.find(s => s.task_id === t.task_id)).length})
                      </Button>
                    )}
                  </div>

                  {/* Add new task inline */}
                  <div className="flex gap-2">
                    <Input
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      placeholder="Create a new task..."
                      className="text-sm h-8"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddQuickTask(); } }}
                      data-testid="new-task-input"
                    />
                    <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={handleAddQuickTask}>
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400">Tasks assigned to all selected team members</p>
                </div>
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea
                value={newSchedule.notes}
                onChange={(e) => setNewSchedule({ ...newSchedule, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
                data-testid="schedule-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowScheduleModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newSchedule.project_id || !newSchedule.title || !newSchedule.date} data-testid="save-schedule-btn">
                {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Project Modal */}
      <Dialog open={showProjectModal} onOpenChange={setShowProjectModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g., Smith Kitchen Renovation"
                required
                data-testid="project-name"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Project details..."
                rows={2}
                data-testid="project-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Client Name</Label>
                <Input
                  value={newProject.client_name}
                  onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="project-client-name"
                />
              </div>
              <div>
                <Label>Client Phone</Label>
                <Input
                  value={newProject.client_phone}
                  onChange={(e) => setNewProject({ ...newProject, client_phone: e.target.value })}
                  placeholder="+1 555-123-4567"
                  data-testid="project-client-phone"
                />
              </div>
            </div>

            <div>
              <Label>Client Email</Label>
              <Input
                type="email"
                value={newProject.client_email}
                onChange={(e) => setNewProject({ ...newProject, client_email: e.target.value })}
                placeholder="john@example.com"
                data-testid="project-client-email"
              />
            </div>

            <div>
              <Label>Install Address *</Label>
              <Input
                value={newProject.address}
                required minLength={3}
                onChange={(e) => setNewProject({ ...newProject, address: e.target.value })}
                placeholder="123 Main St, Toronto, ON"
                data-testid="project-address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget</Label>
                <Input
                  type="number"
                  value={newProject.budget}
                  onChange={(e) => setNewProject({ ...newProject, budget: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                  data-testid="project-budget"
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-2 mt-1">
                  {["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899"].map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full transition-all ${
                        newProject.color === color ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : ""
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewProject({ ...newProject, color })}
                    />
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowProjectModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newProject.name} data-testid="save-project-btn">
                Create Project
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Picker Modal */}
      <Dialog open={showTaskPickerModal} onOpenChange={setShowTaskPickerModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-indigo-600" />
              Available Tasks
            </DialogTitle>
            <p className="text-sm text-slate-500">Select tasks to assign to this schedule</p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-2 space-y-1">
            {projectTasks.map(task => {
              const isSelected = !!selectedTasks.find(t => t.task_id === task.task_id);
              return (
                <label
                  key={task.task_id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${isSelected ? "bg-indigo-50 border border-indigo-300" : "hover:bg-slate-50 border border-transparent"}`}
                  data-testid={`pick-task-${task.task_id}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleTaskSelection(task.task_id)}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
                    {task.description && <p className="text-xs text-slate-500 truncate">{task.description}</p>}
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className={`text-[10px] ${task.priority === "high" ? "bg-red-100 text-red-700" : task.priority === "medium" ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-600"}`}>{task.priority}</Badge>
                      <Badge className={`text-[10px] ${task.status === "completed" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>{task.status}</Badge>
                      {task.assignees?.length > 0 && <span className="text-[10px] text-slate-400">{task.assignees.length} assigned</span>}
                    </div>
                  </div>
                </label>
              );
            })}
            {projectTasks.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm">No tasks found for this project</div>
            )}
          </div>
          <DialogFooter className="shrink-0 border-t pt-3">
            <div className="flex items-center justify-between w-full">
              <span className="text-sm text-slate-500">{selectedTasks.filter(t => !t.isNew).length} selected</span>
              <Button onClick={() => setShowTaskPickerModal(false)} data-testid="done-picking-tasks">Done</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day Detail Modal */}
      <Dialog open={!!selectedDay} onOpenChange={(v) => { if (!v) setSelectedDay(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          {selectedDay && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>{new Date(selectedDay.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</span>
                  {canCreate && (
                    <Button size="sm" onClick={() => { setSelectedDay(null); setNewSchedule(s => ({ ...s, date: selectedDay.date })); setShowScheduleModal(true); }} data-testid="day-add-new">
                      <Plus className="w-3 h-3 mr-1" /> Add New
                    </Button>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {selectedDay.events.length > 0 ? selectedDay.events.map(ev => {
                  const project = projects.find(p => p.project_id === ev.project_id);
                  return (
                    <div
                      key={ev.schedule_id}
                      className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => { setSelectedDay(null); handleEditSchedule(ev); }}
                      style={{ borderLeftColor: ev.color || project?.color || "#6366f1", borderLeftWidth: 4 }}
                      data-testid={`day-event-${ev.schedule_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-slate-900">{ev.title}</h3>
                        <Badge className="text-xs" style={{ backgroundColor: ev.color || project?.color || "#6366f1", color: "white" }}>
                          {ev.start_time} - {ev.end_time}
                        </Badge>
                      </div>
                      {project && <p className="text-sm text-slate-600 mt-1">{project.name}</p>}
                      {ev.notes && <p className="text-xs text-slate-500 mt-1 truncate">{ev.notes}</p>}
                      {ev.assigned_users?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-1">{ev.assigned_users.length} assigned</p>
                      )}
                    </div>
                  );
                }) : (
                  <div className="text-center py-8 text-slate-400">
                    <CalendarIcon className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>Nothing scheduled</p>
                    {canCreate && <p className="text-xs mt-1">Click "+ Add New" to create a schedule</p>}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EventTasks({ scheduleId, projectId }) {
  const [tasks, setTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      try {
        // Get tasks for this schedule
        const res = await axios.get(`${API}/tasks?schedule_id=${scheduleId}`).catch(() => ({ data: [] }));
        let allTasks = res.data || [];
        // Also get project tasks assigned to this schedule
        if (projectId && allTasks.length === 0) {
          const projRes = await axios.get(`${API}/tasks/project-all/${projectId}`).catch(() => ({ data: [] }));
          allTasks = (projRes.data || []).filter(t => t.schedule_id === scheduleId);
        }
        setTasks(allTasks);
      } catch (err) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    if (scheduleId) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, projectId]);

  if (loading) return null;
  if (tasks.length === 0) return null;

  return (
    <div>
      <Label className="text-slate-500">Tasks ({tasks.length})</Label>
      <div className="space-y-1 mt-2">
        {tasks.map(task => (
          <div key={task.task_id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
            <CheckSquare className={`w-4 h-4 shrink-0 ${task.status === "completed" ? "text-green-600" : "text-slate-400"}`} />
            <span className={task.status === "completed" ? "line-through text-slate-400" : "text-slate-700"}>{task.title}</span>
            {task.priority === "high" && <Badge className="text-[10px] bg-red-100 text-red-700 ml-auto">High</Badge>}
          </div>
        ))}
      </div>
    </div>
  );
}
